/**
 * State Style02 - 緊湊資訊列：icon + value + lifecycle
 */
import type { StateControlProps, StateLifecycle } from './stateStyleTypes'
import {
  formatStateValue,
  getStateConfig,
  getStateLifecycleColor,
  inferStateLifecycle,
} from './traitStyleConfig'
import './StateStyle02.css'

const LIFECYCLE_LABEL: Record<StateLifecycle, string> = {
  live: 'Live',
  warn: 'Warn',
  error: 'Error',
  stale: 'Stale',
  offline: 'Offline',
}

export function StateStyle02({
  stateKey,
  value,
  unit = '',
  label,
  icon,
  lifecycle,
  updatedAtLabel,
  precision,
  thresholdEnabled,
  disabled = false,
}: StateControlProps) {
  const config = getStateConfig(stateKey, { icon })
  const displayValue = formatStateValue(value, precision)
  const displayLabel = label ?? config.label ?? stateKey
  const resolvedLifecycle =
    lifecycle ??
    (disabled
      ? 'offline'
      : inferStateLifecycle(stateKey, value, {
          thresholdEnabled,
        }))
  const lifecycleColor = getStateLifecycleColor(resolvedLifecycle)
  const lifecycleText = LIFECYCLE_LABEL[resolvedLifecycle]
  const updatedLabel = String(updatedAtLabel || '2m 前更新')

  return (
    <div
      className="trait-state-style02 trait-stitch-glass"
      data-css-slot="stateContainer"
      data-css-slot-label="State Container"
      data-css-state={resolvedLifecycle}
    >
      <span
        className="trait-state-style02__icon"
        style={{ color: lifecycleColor }}
        data-css-slot="stateIcon"
        data-css-slot-label="State Icon"
        data-css-state={resolvedLifecycle}
      >
        <span className="material-symbols-rounded">{config.icon}</span>
      </span>
      <div className="trait-state-style02__content">
        <span
          className="trait-state-style02__label"
          data-css-slot="stateLabel"
          data-css-slot-label="State Label"
          data-css-state={resolvedLifecycle}
        >
          {displayLabel}
        </span>
        <div className="trait-state-style02__value-row">
          <span
            className="trait-state-style02__value"
            style={{ color: config.color }}
            data-css-slot="stateValue"
            data-css-slot-label="State Value"
            data-css-state={resolvedLifecycle}
          >
            {displayValue}
          </span>
          {unit ? (
            <span
              className="trait-state-style02__unit"
              data-css-slot="stateUnit"
              data-css-slot-label="State Unit"
              data-css-state={resolvedLifecycle}
            >
              {unit}
            </span>
          ) : null}
        </div>
      </div>
      <div className="trait-state-style02__meta">
        <span
          className="trait-state-style02__badge"
          style={{ '--state-lifecycle-color': lifecycleColor } as React.CSSProperties}
          data-css-slot="stateBadge"
          data-css-slot-label="State Lifecycle Badge"
          data-css-state={resolvedLifecycle}
        >
          {lifecycleText}
        </span>
        <span
          className="trait-state-style02__updated"
          data-css-slot="stateUpdatedAt"
          data-css-slot-label="State Updated Time"
          data-css-state={resolvedLifecycle}
        >
          {updatedLabel}
        </span>
      </div>
    </div>
  )
}
